import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileImage, Trash2, X, Pin, RefreshCw, AlertTriangle } from 'lucide-react';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import toast from 'react-hot-toast';

const Button = ({ children, onClick, variant = 'primary', className = '', disabled = false, icon, ...props }) => {
  const variantStyles = {
    primary:
      'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50',
    destructive:
      'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-opacity-50',
    outline:
      'border border-zinc-600 text-zinc-300 hover:bg-zinc-800 focus:ring-2 focus:ring-zinc-500 focus:ring-opacity-50',
    success:
      'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        flex items-center justify-center gap-2 px-4 py-2 rounded-lg 
        transition-all duration-200 ease-in-out 
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variantStyles[variant]}
        ${className}
      `}
      {...props}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {children}
    </button>
  );
};

// Delete confirmation modal component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, imageFileName }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 transition-opacity">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden transform transition-all">
        <div className="p-6">
          <div className="flex items-center justify-center mb-5">
            <div className="bg-red-500 bg-opacity-20 rounded-full p-3">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
          </div>
          
          <h3 className="text-lg font-medium text-zinc-100 text-center mb-2">Confirm Deletion</h3>
          
          <p className="text-zinc-300 text-center mb-6">
            Are you sure you want to delete {imageFileName ? <span className="font-medium">{imageFileName}</span> : "this image"}? 
            This action cannot be undone.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button 
              onClick={onConfirm} 
              variant="destructive" 
              className="order-2 sm:order-1"
              icon={<Trash2 size={16} />}
            >
              Delete Image
            </Button>
            
            <Button 
              onClick={onClose} 
              variant="outline" 
              className="order-1 sm:order-2"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageCard = ({ gif, onDelete, onToggleSticky }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };
  
  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete(gif.id);
  };
  
  return (
    <div
      className="relative rounded-lg h-fit overflow-hidden border border-zinc-800 bg-zinc-900
                transition-all duration-200 hover:border-zinc-600 hover:shadow-xl"
    >
      <div className="flex items-center justify-center bg-zinc-950 p-2">
        <img
          src={gif.url}
          alt={gif.fileName || 'Uploaded Image'}
          className="max-w-full max-h-full object-contain transition-transform duration-300 hover:scale-105"
        />
      </div>
      
      <div className={`absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-between p-3
                      transition-opacity duration-200 opacity-0 hover:opacity-100`}>
        {/* Top Controls */}
        <div className="flex justify-between">
          <button
            onClick={() => onToggleSticky(gif.id, gif.sticky)}
            className={`p-2 rounded-full shadow-lg transition-colors duration-200
                      ${gif.sticky ? 'bg-blue-600 hover:bg-blue-700' : 'bg-zinc-800 hover:bg-zinc-700'}`}
            title={gif.sticky ? 'Unpin Image' : 'Pin Image'}
          >
            <Pin size={16} className={gif.sticky ? 'fill-current' : ''} />
          </button>
          
          <button
            onClick={handleDeleteClick}
            className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
            title="Delete Image"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      {/* Delete confirmation modal */}
      <DeleteConfirmationModal 
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        imageFileName={gif.fileName}
      />
    </div>
  );
};

const UploadPreview = ({ uploadGif, onUpload, onCancel }) => {
  return (
    <div className="p-4 sm:p-6 border-b border-zinc-700 bg-zinc-900 bg-opacity-40 rounded-lg">
      <h3 className="text-lg font-medium mb-4 text-zinc-100">Upload Preview</h3>
      
      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-2/5 lg:w-1/3">
          <div className="relative rounded-lg overflow-hidden border border-zinc-700 bg-black">
            <div className="flex items-center justify-center h-64">
              <img
                src={uploadGif.url}
                alt="GIF Preview"
                className="max-w-full max-h-full object-contain"
              />
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-80 py-2 px-3 text-sm truncate">
              {uploadGif.fileName}
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-4 w-full md:w-auto md:justify-center">
          <p className="text-zinc-300 text-sm mb-2">
            Review your image before uploading
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onUpload}
              variant="success"
              className="sm:w-auto px-4 py-2.5 font-medium"
              icon={<Upload size={18} />}
            >
              Upload Image
            </Button>
            
            <Button
              onClick={onCancel}
              variant="outline"
              className="sm:w-auto px-4 py-2.5"
              icon={<X size={18} />}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ onSelect }) => (
  <div className="text-center py-12 rounded-lg border border-dashed border-zinc-700 
                  text-zinc-400 bg-zinc-900 bg-opacity-50 transition-all duration-300
                  hover:border-zinc-600 hover:bg-opacity-70">
    <FileImage size={48} className="mx-auto mb-4 text-zinc-500" />
    <p className="px-4 mb-6 text-lg">No images uploaded yet</p>
    <Button onClick={onSelect} variant="primary" className="mx-auto" icon={<Upload size={18} />}>
      Select an Image
    </Button>
  </div>
);

const LoadingState = () => (
  <div className="text-center py-16">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
    <p className="mt-6 text-zinc-400">Loading images...</p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className="text-center py-12 rounded-lg border border-red-900 bg-red-900 bg-opacity-10 text-red-300">
    <div className="mb-4">
      <X size={40} className="mx-auto text-red-500" />
    </div>
    <p className="mb-6">{error}</p>
    <Button onClick={onRetry} variant="outline" className="mx-auto" icon={<RefreshCw size={16} />}>
      Try Again
    </Button>
  </div>
);

const ManageAds = () => {
  const [uploadGif, setUploadGif] = useState(null);
  const [uploadedGifs, setUploadedGifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filterSticky, setFilterSticky] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchUploadedGifs();
  }, []);

  const fetchUploadedGifs = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://backend.takeuforward.org/admin/api/blogs/getAllAds');
      if (!response.ok) {
        throw new Error(`Failed to fetch images: ${response.status}`);
      }
      const data = await response.json();
      setUploadedGifs(data.ads);
      setError(null);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load images. Please try again later.');
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setUploadGif({
        id: Date.now().toString(),
        file: file,
        url: URL.createObjectURL(file),
        fileName: file.name,
      });
      toast.success('Image selected successfully');
    } else if (file) {
      toast.error('Please select a valid image file');
    }
    event.target.value = null;
  };

  const handleCancelUpload = () => {
    if (uploadGif?.url) {
      URL.revokeObjectURL(uploadGif.url);
    }
    setUploadGif(null);
    toast('Upload cancelled', { icon: '❌' });
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleUploadGif = async () => {
    if (!uploadGif) return;

    toast.loading('Uploading image...', { id: 'uploadGif' });

    try {
      const formData = new FormData();
      formData.append('file', uploadGif.file);

      const response = await fetch('https://backend.takeuforward.org/admin/api/blogs/uploadAd', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      fetchUploadedGifs();
      setUploadGif(null);

      toast.success('Image uploaded successfully', { id: 'uploadGif' });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image. Please try again.', { id: 'uploadGif' });
    }
  };

  const handleDeleteGif = async (gifId) => {
    if (!gifId) return;

    toast.loading('Deleting image...', { id: 'deleteGif' });

    try {
      const response = await fetch(
        `https://backend.takeuforward.org/admin/api/blogs/deleteAd/${gifId}`,
        {
          method: 'DELETE',
        },
      );

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      const result = await response.json();
      console.log('Delete successful:', result);

      fetchUploadedGifs();

      toast.success('Image deleted successfully', { id: 'deleteGif' });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete image. Please try again.', { id: 'deleteGif' });
    }
  };

  const handleToggleSticky = async (gifId, currentSticky) => {
    try {
      const response = await fetch(
        `https://backend.takeuforward.org/admin/api/blogs/updateAdSticky/${gifId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sticky: currentSticky ? 0 : 1 }),
        },
      );

      if (!response.ok) {
        throw new Error('Update failed');
      }

      fetchUploadedGifs();
      toast.success(`Image ${currentSticky ? 'unpinned' : 'pinned'} successfully`);
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update image status. Please try again.');
    }
  };

  // Filter images based on sticky status if filter is active
  const filteredGifs = filterSticky 
    ? uploadedGifs.filter(gif => gif.sticky) 
    : uploadedGifs;

  return (
    <div className='flex text-zinc-200 h-screen overflow-hidden font-sans'>
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className='relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden h-full'>
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className='p-4 sm:p-6 md:p-8'>
          <div className='mx-auto h-full max-w-7xl'>
            <div className='bg-[#1E1E1E] rounded-xl md:rounded-2xl border border-zinc-800 overflow-hidden h-full shadow-lg'>
              {/* Header */}
              <div className='px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between border-b border-zinc-700 gap-3 bg-zinc-900'>
                <h2 className='text-xl sm:text-2xl font-bold text-zinc-100 flex items-center gap-2'>
                  <FileImage className='text-blue-400' size={24} />
                  Manage Ad Images
                </h2>

                <div className='flex items-center gap-3'>
                  <input
                    type='file'
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept='image/*'
                    className='hidden'
                  />
                  
                  <Button
                    onClick={() => setFilterSticky(!filterSticky)}
                    variant={filterSticky ? 'primary' : 'outline'}
                    className='hover:bg-zinc-700'
                    icon={<Pin size={16} className={filterSticky ? 'fill-current' : ''} />}
                  >
                    {filterSticky ? 'Showing Pinned' : 'Show Pinned'}
                  </Button>
                  
                  <Button
                    onClick={triggerFileInput}
                    variant='primary'
                    className='hover:bg-blue-500'
                    icon={<Upload size={16} />}
                  >
                    <span>Upload New</span>
                  </Button>
                </div>
              </div>

              {/* Upload Preview */}
              {uploadGif && (
                <div className="p-4 sm:p-6">
                  <UploadPreview 
                    uploadGif={uploadGif} 
                    onUpload={handleUploadGif} 
                    onCancel={handleCancelUpload} 
                  />
                </div>
              )}

              {/* Main Content */}
              <div className='p-4 sm:p-6 overflow-y-auto h-full'>
                {/* Title with count */}
                <div className='flex items-center justify-between mb-6'>
                  <h3 className='text-lg font-semibold flex items-center gap-2'>
                    Uploaded Images
                    {filterSticky && <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">Pinned only</span>}
                  </h3>
                  
                  {!loading && !error && filteredGifs.length > 0 && (
                    <span className='text-sm text-zinc-400 bg-zinc-800 px-3 py-1 rounded-full'>
                      {filteredGifs.length} image{filteredGifs.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Content States */}
                {loading ? (
                  <LoadingState />
                ) : error ? (
                  <ErrorState error={error} onRetry={fetchUploadedGifs} />
                ) : filteredGifs.length === 0 ? (
                  filterSticky ? (
                    <div className="text-center py-8 text-zinc-400">
                      <Pin size={32} className="mx-auto mb-3 text-zinc-500" />
                      <p>No pinned images found.</p>
                      <Button 
                        onClick={() => setFilterSticky(false)} 
                        variant="outline" 
                        className="mt-4 mx-auto"
                      >
                        Show All Images
                      </Button>
                    </div>
                  ) : (
                    <EmptyState onSelect={triggerFileInput} />
                  )
                ) : (
                  <div className='grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-5 overflow-y-auto'>
                    {filteredGifs.map((gif) => (
                      <ImageCard 
                        key={gif.id} 
                        gif={gif} 
                        onDelete={handleDeleteGif} 
                        onToggleSticky={handleToggleSticky} 
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ManageAds;
